import { inject, Injectable } from '@angular/core';
import {
	PluginType,
	StudyMaterialSummaryGameData,
	StudyMaterialSummaryEditorTopic,
	StudyMaterialSummaryEditorParagraph,
	TextRendererGameData,
	ImageGameData,
	ImageWithCaptions,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
	GenerationStep,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';
import { SourceMaterialService } from '../../source-material-service/source-material.service';
import { AiService } from '../ai/ai.service';

/**
 * Enum for different content generation scenarios
 */
export enum ContentGenerationType {
	NEW_CONTENT_WITH_SOURCE = 'newContentWithSource',
	NEW_CONTENT_WITHOUT_SOURCE = 'newContentWithoutSource',
	UPDATE_EXISTING_WITH_SOURCE = 'updateExistingWithSource',
	UPDATE_EXISTING_WITHOUT_SOURCE = 'updateExistingWithoutSource',
	UPDATE_WITH_CUSTOM_REQUEST = 'updateWithCustomRequest',
}

/**
 * Parameters for text content generation
 */
export interface TextContentGenerationParams {
	generationType: ContentGenerationType;
	topicTitle: string;
	mainTitle: string;
	topicSummary?: string;
	existingParagraphs?: any[];
	selectedConcepts?: any[];
	customRequest?: string;
	allTopics?: any[]; // For context about other topics
	allowMultipleParagraphs: boolean;
	imageMetadata?: string;
	context?: string;
}

/**
 * Study Material Summary specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class StudyMaterialSummaryGenerationService extends BaseGenerationService<StudyMaterialSummaryGameData> {
	readonly pluginType = PluginType.StudyMaterialSummary;

	sourceMaterialService: SourceMaterialService = inject(
		SourceMaterialService,
	);

	aiService: AiService = inject(AiService);

	/**
	 * Extract main title from source material for auto-generation
	 * @param sourceMaterial The source material to analyze
	 * @param settingsService Settings service for language preferences
	 * @returns Single most relevant title
	 */
	extractMainTitle(
		sourceMaterial: any,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		const systemPrompt = `Olet asiantuntija, joka analysoi annetun tekstin ja luo sille ytimekkään otsikon.
Tehtäväsi on tunnistaa tekstin pääaihe ja luoda siitä selkeä, kuvaava otsikko.

`;

		const includeImages = sourceMaterial?.imagesAsString
			? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna otsikon laatimisessa:\n${sourceMaterial.imagesAsString}`
			: '';

		let userPrompt = `Analysoi annettu lähdemateriaali ja luo sille yksi selkeä pääotsikko, joka kuvaa materiaalin keskeisintä aihetta.${includeImages}

Noudata seuraavia ohjeita:
- Luo vain yksi otsikko
- Otsikon tulee olla lyhyt ja ytimekäs (maksimissaan 8 sanaa)
- Otsikon tulee kuvata materiaalin pääaihetta
- Älä käytä erikoismerkkejä tai numerointia

Vastaa JSON-muodossa:
{
    "title": "Materiaalin pääotsikko"
}`;

		userPrompt += `
		
		${targetLanguagePrompt}
		${targetDifficultyPrompt}
		`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Generate complete study material structure automatically
	 * Creates title, summary, and topics with content from source material or custom topic
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts for full structure generation
	 */
	createFullStructurePrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;
		const customTopic = parameters['customTopic'] || '';
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		const systemPrompt = `Olet opetusmateriaalin kirjoittaja. Tehtäväsi on luoda täydellinen oppimateriaalirakenne annetusta aiheesta tai lähdemateriaalista. 
Luo johdonmukainen kokonaisuus, joka sisältää pääotsikon, tiivistelmän ja sisällysluettelon otsikoilla.
Lukijan tulee pystyä hahmottamaan koko aihe selkeästi ja oppimaan asia järjestelmällisesti.

`;

		const hasSourceMaterial =
			sourceMaterial && sourceMaterial.fileHandles.length > 0;

		let userPrompt = hasSourceMaterial
			? `Analysoi annettu lähdemateriaali ja luo siitä täydellinen oppimateriaalirakenne.
			
Käytä tiedostohakua ja etsi kaikista tiedostoista sen käsittelemät aiheet.
Yhdistä kaikki löytämäsi aiheet ja kirjoita niistä koherentti oppimateriaali.
`
			: `Luo täydellinen oppimateriaalirakenne aiheesta: "${customTopic || 'Yleinen oppimateriaali'}. Käytä parasta tietämystäsi aiheesta."

Luo johdonmukainen oppimateriaali:
1. Pääotsikko, joka kuvaa aihetta selkeästi
2. Tiivistelmä (2-3 lausetta), joka kuvaa mitä oppimateriaalissa käsitellään
3. 3-7 pääotsikkoa, jotka kattavat aiheen tärkeimmät osa-alueet
4. Jokaisen pääotsikon alle 2-4 alaotsikkoa (intendation 1). Jokaisessa alaotsikko-objektissa tulee olla paragraphs-kenttä, jossa on yksi tekstikappale (gameData).
5. Tekstikappaleen tulee sisältää kaikki oleellinen otsikon aiheesta. Kirjoita kappale suhteessa kokonaisuuteen niin, että otsikot, alaotsikot ja kappaleet yhdessä muodostavat selkeän ja johdonmukaisen kokonaisuuden.

Anna vastaus JSON-muodossa:
{
    "title": "${customTopic || 'Oppimateriaali'}",
    "summary": "Tiivistelmä aiheesta",
    "topics": [
        {"title": "Pääotsikko 1", "intendation": 0, paragraphs: [] }, // Jätä paragraphs tyhjäksi
        {"title": "Alaotsikko 1.1", "intendation": 1,
			paragraphs: [
				{
					gameData: {
						type: 10,
						text: 'Kappaleen teksti...'
					}
				}
			]
		}, // Paragraphs-kentässä on pakko olla yksi kappale tekstiä.
		{"title": "Alaotsikko 1.2", "intendation": 1, paragraphs: [ /* Yksi kappale, kuten kuvattu edellä */] },
        {"title": "Pääotsikko 2", "intendation": 0},
        ...
    ]
}
	`;

		userPrompt += `
	
	
	${targetLanguagePrompt}
	${targetDifficultyPrompt}	`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for paragraph length modification
	 * @param request The generation request with paragraphText and operation type
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts for paragraph modification
	 */
	createParagraphLengthPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;
		const operation = parameters['operation'] || 'decrease'; // 'decrease' or 'increase'
		const paragraphText = parameters['paragraphText'] || '';
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt: string;
		let userPrompt: string;

		if (operation === 'decrease') {
			systemPrompt = `Olet opetusmateriaalin päätoimittaja. Tehtäväsi on opetusmateriaalia niin, että tieto säilyy mahdollisimman hyvin. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}`;

			userPrompt = `Tiivistä seuraava teksti puoleen pituuteen niin, että pääajatus säilyy:

"${paragraphText}"

Palauta JSON-objekti, jossa on seuraavat kentät:
- "modifiedText" (string) Tiivistetty teksti, joka on noin puolet alkuperäisestä pituudesta mutta säilyttää tärkeimmät tiedot.`;
		} else {
			systemPrompt = `Olet opetusmateriaalin päätoimittaja. Tehtäväsi on täydentää opetusmateriaalia niin, että lisäät siihen mahdollisimman paljon yksityiskohtia. Käytä <p>-tageja tekstin muotoiluun sopiviksi kappaleiksi. Anna vastaus validoidussa JSON-muodossa.

`;

			userPrompt = `Laajenna seuraava teksti kaksinkertaiseksi lisäämällä yksityiskohtia, esimerkkejä ja selityksiä:

"${paragraphText}"

Palauta JSON-objekti, jossa on seuraavat kentät:
- "modifiedText" (string) Laajennettu teksti, joka on noin kaksinkertainen alkuperäisestä ja sisältää lisää yksityiskohtia, esimerkkejä ja selityksiä. Käytä <p>-tageja jakamaan teksti kappaleisiin.`;
		}

		userPrompt += `

		${targetLanguagePrompt}
		${targetDifficultyPrompt}
		`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create system and user prompts for title suggestions generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createTitleSuggestionPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		const systemPrompt = `Olet oppimateriaali-asiantuntuja. Tehtäväsi on ehdottaa sopivia otsikoita oppimateriaalille annetun lähdemateriaalin pohjalta. Analysoi lähdemateriaalin sisältö ja ehdota 5-8 lyhyttä, ytimekästä otsikkoa mukana lyhyet tiivistelmät, jotka kuvaavat materiaalin pääsisältöä. Otsikkojen tulee olla informatiivisia ja kiinnostavia. Tiivistelmien tulee olla lyhyitä (1-2 lausetta) ja kuvata, mitä kyseisestä otsikosta voisi odottaa löytävän. Suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä. Anna vastaus validoidussa JSON-muodossa.

`;

		const userPrompt = `
		Analysoi annettua lähdemateriaalia ja ehdota sopivia otsikoita oppimateriaalille. Jokainen ehdotus tulee sisältää sekä otsikon että lyhyen tiivistelmän. Otsikkojen tulee:
		- Olla ytimekkäitä ja informatiivisia
		- Kuvata materiaalin pääsisältöä
		- Olla kiinnostavia lukijalle
		- Noudattaa valitun kielen kielioppia (esim. suomessa vain ensimmäinen alkukirjain isolla, englannissa kaikkien sanojen alkukirjaimet isolla, paitsi prepositiot jne.)
		
		Tiivistelmien tulee:
		- Olla lyhyitä (1-2 lausetta)
		- Kuvata, mitä otsikon mukaista sisältöä materiaalista löytyy
		- Antaa lukijalle selkeä käsitys siitä, mistä kyseisessä materiaalissa on kyse
		
		Anna vastaus JSON-muodossa:
		{
			"suggestions": [
				{
					"title": "Otsikko1" // valitun kielen kieliopin mukaan,
					"summary": "lyhyt tiivistelmä joka kuvaa otsikon sisältöä"
				},
				{
					"title": "Otsikko2", // valitun kielen kieliopin mukaan
					"summary": "lyhyt tiivistelmä joka kuvaa otsikon sisältöä"
				}
			]
		}
			
		${targetLanguagePrompt}
		${targetDifficultyPrompt}
		`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create system and user prompts for topics generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createTopicsPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
		generateTopicsPromptsWithParagraphs: boolean = false,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;
		const generateSubtopics = parameters['generateSubtopics'] || false;
		const customPrompt = parameters['customPrompt'] || '';
		const context = parameters['context'] || '';
		const title = parameters['title'] || '';
		const summary = parameters['summary'] || '';
		const existingTopics = parameters['existingTopics'] || [];

		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja. Tehtäväsi on kirjoittaa johdonmukainen sisällysluettelo lähdemateriaalin pohjalta. Jos lähdemateriaalia ei ole annettu, kirjoita parhaan tietämyksesi mukaan annetusta aiheesta. Lukijan tulee sisällysluettelon avulla pystyä hahmottamaan koko lähdemateriaalin sisältö ja edetä sen läpi johdonmukaisesti, asia kerrallaan. oppia asia kirjoittamasi tekstin pohjalta. Ole siis johdonmukainen ja sisällytä mukaan mahdollisimman paljon lähdemateriaalia, jos sitä on saatavilla. Huomaa, että suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä. Anna vastaus validoidussa JSON-muodossa.
`;

		// Add context, if any
		if (context !== '') {
			systemPrompt += `Tässä on materiaalin konteksti, joka kuvaa sitä, mistä on kyse. On tärkeää, että kaikki analyysi tehdään suhteessa kontekstiin. <context>${context}</context>`;
		}

		if (customPrompt !== '') {
			systemPrompt += `Tässä on käyttäjän antama ohjeistus, joka kuvaa sitä, mitä käyttäjä haluaa. On tärkeää, että kaikki analyysi tehdään suhteessa käyttäjän antamaan ohjeistukseen. <customPrompt>${customPrompt}</customPrompt>`;
		}

		const hasSourceMaterial =
			sourceMaterial && sourceMaterial.fileHandles.length > 0;

		let userPrompt = hasSourceMaterial
			? this.createTopicsPromptWithSourceMaterial(
					generateSubtopics,
					title,
					summary,
					existingTopics,
					generateTopicsPromptsWithParagraphs,
				)
			: this.createTopicsPromptWithoutSourceMaterial(
					generateSubtopics,
					title,
					summary,
					existingTopics,
					generateTopicsPromptsWithParagraphs,
				);

		userPrompt += `
		
		${targetLanguagePrompt}
		${targetDifficultyPrompt}`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create user prompt for topics generation with source material
	 */
	private createTopicsPromptWithSourceMaterial(
		generateSubtopics: boolean,
		title: string,
		summary: string,
		existingTopics: any[],
		generateTopicsPromptsWithParagraphs: boolean,
	): string {
		const targetLanguagePrompt =
			this.settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			this.settingsService.GetTargetDifficultyPrompt();

		return `
            Käytä tiedostohakua (file_search_tool) ja etsi kaikista tiedostoista sen käsittelemät aiheet. Yhdistä kaikki löytämäsi aiheet, päättele, mistä kokonaisuus kertoo ja kirjoita niistä koherentti sisällysluettelo.
            
            Käyttäjä saattaa antaa sinulle kokonaisuuden pääotsakkeen ja tiivistelmän valmiina. Jos näin on, kirjoita otsakkeet niin, että ne sopivat pääotsakkeeseen ja sen tiivistelmään. Jos pääotsake tai tiivistelmä puuttuu, saat kirjoittaa ne itse.
			
			Osa otsakkeista saattaa olla jo valmiina. Jos osa otsakkeista on annettu, ota ne mukaan sellaisenaan. Älä muuta niitä. Voit kuitenkin muuttaa otsikoiden järjestystä niin, että otsikot ovat loogisessa järjestyksessä asiakokonaisuuden suhteen. Voit lisätä myös uusia otsakkeita väleihin.
			
			Alaotsikointi:
            ${
				generateSubtopics
					? `
            Luo jokaiselle otsakkeelle tarkat alaotsikot sen mukaan, mitä aihetta sen alla käsitellään. Alaotsikko luodaan samoin kuin pääotsikko, mutta sen intendation-kenttään laitetaan arvo 1. Yleensä alaotsikoita on hyvä olla 2-4 per pääotsikko. Eli tulee siis luoda rakenne, kuten
			// { "title": "Pääotsikko", "intendation": 0 },
			// { "title": "Alaotsikko", "intendation": 1 },
			// { "title": "Alaotsikko", "intendation": 1 },
			// { "title": "Pääotsikko", "intendation": 1 }, jne.
			
			On tärkeää, että jokaista alaotsikkoa (intendation 1) edeltää pääotsake, jolla on intendation 0. Sisennystä ei saa tehdä, ellei sitä edellä yhtä pienempi sisennystaso. Ensimmäinen taso on 0.
			
			Jaa pääotsakkeen (intendation 0) aihe alaotsikoihin (intendation 1), joita on aina 2-10 kappaletta, riippuen paljonko aiheen käsittelemiseen tarvitaan.
			`
					: `Älä luo alaotsakkeita, pelkkiä pääotsakkeita. Tämä tarkoittaa, että jokaisen otsikon intendation-muuttujan arvo on 0.`
			}
            
            Sisällysluettelon tulee olla yksityiskohtainen ja kattava. Älä jätä mitään aihetta pois. Käytä saamaasi lähdemateriaalia pääasiallisena lähteenäsi. Sisällytä sisällysluetteloon mahdollisimman paljon tietoa ja yksityiskohtia lähdemateriaalista.
            
            Tee näin:
            1) Pääotsakkeen luominen: Jos pääotsake on olemassa, sinun tulee keksiä kaikki otsikot niin, että ne kuuluvat pääotsikon alle. Älä keksi ylimääräisiä otsikoita vaan pääotsikko toimii rajaavana tekijänä. Pääotsake: "${title !== '' ? title : '[Saat keksiä pääotsakkeen itse materiaalin pohjalta niin, että se sopii materiaaliin. Otsikon tulee olla ytimekäs ja kuvaava]'}".
            
            2) Tee tiivistelmä koko sisällöstä: Jos tiivistelmä on olemassa, käytä tätä vahvana ohjeena yhdessä pääotsikoiden kanssa siihen, mitä otsikoita opetusmateriaaliin ehdotetaan. Tiivistelmä: "${summary !== '' ? summary : '[Saat keksiä tiivistelmän itse materiaalin pohjalta niin, että se sopii materiaaliin.]'}".
            
            Valmiit otsikot, joita ei saa muuttaa, mutta joiden ympärille voit lisätä uusia otsikoita.
            ${JSON.stringify(existingTopics)}
			
			3) Luo otsakkeet (intendation 0)
			
			${generateSubtopics ? '4) Luo alaotsakkeet: Jaa luomasi pääotsakkeet ala-otsakkeisiin (intendation 1, vähintään 2-4 kappaletta KUNKIN OTSAKKEEN JÄLKEEN, joskus enemmänkin)' : ''}
			
			Anna tulokset validoidussa JSON-muodossa:
            // JSON-objektin rakenne on:
            
			"title" // string, opetusmateriaalin pääotsikko. Jos tämä oli annettu valmiina, älä muuta tätä. Jos ei, keksi tämä itse niin, että se kattaa koko annetun materiaalin aihepiirin. Huomaa, että suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä.
            "summary" // string, opetusmateriaalin tiivistelmä. Jos tämä oli annettu valmiina, älä muuta tätä. Jos ei, keksi tämä itse niin, että se kattaa koko annetun materiaalin aihepiirin.
            "topics" // taulukko, jossa jokainen objekti on yksi aihealue
            - "title" // string, aihealueen otsikko. Jos kyseessä on jonkin "title":n alaotsikko, kirjoita alaotsikko tähän. Huomaa, että suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä. Jos seuraava arvo on tosi, jätä tämä kenttä aina tyhjäksi: ${!generateSubtopics ? 'false' : 'true'}.
            - "intendation" // number, otsikon sisennys. Sisennys arvolla 0 tarkoittaa, että kyseessä on pääotsikko. Jos kirjoitat arvoksi 1, tämä tarkoittaa alaotsikkoa. Ennen jokaista alaotsikkoa on oltava pääotsikko eli arvo 0. Alaotsikoita on oltava peräkkäin vähintään kaksi kappaletta.
			}
			
			${targetLanguagePrompt}
			${targetDifficultyPrompt}
            `;
	}

	/**
	 * Create user prompt for topics generation without source material
	 */
	private createTopicsPromptWithoutSourceMaterial(
		generateSubtopics: boolean,
		title: string,
		summary: string,
		existingTopics: any[],
		generateTopicsPromptsWithParagraphs: boolean,
	): string {
		const targetLanguagePrompt =
			this.settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			this.settingsService.GetTargetDifficultyPrompt();
		return `
            Käyttäjä antaa sinulle kokonaisuuden pääotsakkeen ja mahdollisen tiivistelmän valmiina. Kirjoita pääotsakkeeseen sopivat otsakkeet niin, että ne sopivat pääotsakkeeseen ja sen tiivistelmään. Jos pääotsake tai tiivistelmä puuttuu, saat kirjoittaa ne. Osa otsakkeista saattaa olla jo valmiina. Jos osa otsakkeista on annettu, ota ne mukaan sellaisenaan. Voit kuitenkin muuttaa otsikoiden järjestystä niin, että otsikot ovat loogisessa järjestyksessä asiakokonaisuuden suhteen. Voit lisätä myös uusia otsakkeita. Huomaa, että suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä.
			
			Alaotsikointi:
            ${
				generateSubtopics
					? `
            Luo jokaiselle otsakkeelle tarkat alaotsikot sen mukaan, mitä aihetta sen alla käsitellään. Alaotsikko luodaan laittamalla intendation-kenttään arvo 1.
			
			On tärkeää, että jokaista alaotsikkoa (intendation 1 tai 2) edeltää pääotsake, jolla on intendation 0. Sisennystä ei saa tehdä, ellei sitä edellä yhtä pienempi sisennystaso. Ensimmäinen taso on 0.`
					: `Älä luo alaotsakkeita, pelkkiä pääotsakkeita. Tämä tarkoittaa, että jokaisen otsikon intendation-muuttujan arvo on 0.`
			}

            Sisällysluettelon tulee olla yksityiskohtainen ja kattava. Älä jätä mitään aihetta pois. Sisällytä sisällysluetteloon mahdollisimman paljon tietoa ja yksityiskohtia annetusta pääotsakkeesta niin, että sisällysluettelo muodostaa mahdollisimman kokonaisen ja monipuolisen kuvan pääotsakkeen aiheesta..
            
            Ennakkotiedot:
            Tässä ohjeet pääotsaketta varten. Jos pääotsikko on olemassa, sinun tulee keksiä kaikki otsikot niin, että ne kuuluvat pääotsikon alle. Älä keksi aiheen ulkopuolisia otsikoita vaan pääotsikko toimii rajaavana tekijänä. Pääotsikko: "${title !== '' ? title : '[Saat keksiä pääotsakkeen itse materiaalin pohjalta niin, että se sopii materiaaliin.]'}".
            
            Tässä ohjeet tiivistelmää varten. Jos tiivistelmä on olemassa, käytä tätä vahvana ohjeena yhdessä pääotsikoiden kanssa siihen, mitä otsikoita opetusmateriaaliin ehdotetaan. Tiivistelmä: "${summary !== '' ? summary : '[Saat keksiä tiivistelmän itse materiaalin pohjalta niin, että se sopii pääotsikkoon ja valmiina annettuihin alaotsikoihin.]'}".
            
            ${
				existingTopics.length > 0
					? 'Valmiit otsikot, joita ei saa muuttaa, mutta joiden ympärille voit lisätä uusia otsikoita: ' +
						JSON.stringify(existingTopics)
					: ''
			}
            
            Anna vastaus validoidussa JSON-muodossa:
            // JSON-avaimet:
            // "title" // string, opetusmateriaalin pääotsikko. Älä muuta tätä.
            // "summary" // string, opetusmateriaalin tiivistelmä. Jos tämä oli annettu valmiina, älä muuta tätä. Jos ei, keksi tämä itse niin, että se kuvaa otsikkoa ${title}.
            // "topics" // taulukko, jossa jokainen objekti on yksi aihealue
            //  - "title" // string, aihealueen otsikko. 
			// jos kyseessä on jonkin "title":n alaotsikko, kirjoita alaotsikko tähän. Huomaa, että suomenkielessä otsikoissa vain ensimmäinen alkukirjain on isolla, muut pienellä. Jos seuraava arvo on tosi, jätä tämä kenttä aina tyhjäksi: ${!generateSubtopics ? 'false' : 'true'}.
            
            //  - "intendation" // number, otsikon sisennys. Sisennys arvolla 0 tarkoittaa, että kyseessä on pääotsikko. Jos kirjoitat arvoksi 1, tämä tarkoittaa alaotsikkoa. Ennen jokaista alaotsikkoa on oltava pääotsikko eli arvo 0.${
				generateTopicsPromptsWithParagraphs
					? `
            //  - "paragraphs" // taulukko, jossa jokainen objekti sisältää yhden tai useamman kappaleen tätä otsikkoa varten. Lue kappaleita niin monta kuin tarpeen, että kokonaisuus on luettava (human-readable). PAKOLLINEN kun generoidaan sisältöä.
            //    - "gameData" // objekti, joka sisältää kappaleen tiedot
			//      - "type" // number, aina 10 (tekstikappale)
            //      - "text" // string, kappaleen tekstisisältö, joka sisältää kaiken oleellisen tietämyksen tästä otsikonaiheesta. Kirjoita kappale suhteessa kokonaisuuteen niin, että otsikot, alaotsikot ja kappaleet yhdessä muodostavat selkeän ja johdonmukaisen kokonaisuuden.`
					: ''
			}
            
			${targetLanguagePrompt}
			${targetDifficultyPrompt}
            `;
	}

	/**
	 * Create system and user prompts for study material summary generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;

		// Handle multi-step generation step types (new architecture)
		if (parameters['stepType']) {
			switch (parameters['stepType']) {
				case 'generateTopics':
					// Step 1: Generate topics structure
					return this.createTopicsPrompts(request, settingsService);
				case 'generateContent':
					// Step 2: Generate content for topics
					return this.createContentGenerationPrompts(
						request,
						settingsService,
					);
				case 'generate':
					// Single-step generation (legacy plugins)
					// Fall through to existing logic
					break;
				default:
					throw new Error(
						`Unknown stepType: ${parameters['stepType']}`,
					);
			}
		}

		// For auto-generation, use extractMainTitle (simple title only)
		if (parameters['autoGeneration']) {
			return this.extractMainTitle(
				request.sourceMaterial,
				settingsService,
			);
		}

		// For full structure auto-generation
		if (parameters['fullStructure']) {
			return this.createFullStructurePrompts(request, settingsService);
		}

		// Check stepType first (modern multi-step approach), then fall back to parameter flags (backward compatibility)
		const stepType = parameters['stepType'];

		// For title suggestions generation
		if (
			stepType === 'generateTitleSuggestions' ||
			parameters['generateTitleSuggestions']
		) {
			return this.createTitleSuggestionPrompts(request, settingsService);
		}

		// For topics generation
		if (stepType === 'generateTopics' || parameters['generateTopics']) {
			return this.createTopicsPrompts(request, settingsService);
		}

		// For content generation (multi-request generation for empty topics)
		if (stepType === 'generateContent' || parameters['generateContent']) {
			return this.createContentGenerationPrompts(
				request,
				settingsService,
			);
		}

		// For paragraph length modification
		if (
			stepType === 'modifyParagraphLength' ||
			parameters['modifyParagraphLength']
		) {
			return this.createParagraphLengthPrompts(request, settingsService);
		}

		// For comprehensive title and full structure generation
		if (
			stepType === 'titleAndFullStructure' ||
			parameters['titleAndFullStructure']
		) {
			return this.createTitleAndFullStructure(request, settingsService);
		}

		// For text content generation (new method for h2/h3 content)
		if (
			stepType === 'generateTextContent' ||
			parameters['generateTextContent']
		) {
			return this.createTextContentPrompts(request, settingsService);
		}

		// For paragraph/content generation (legacy)
		if (
			stepType === 'generateParagraphs' ||
			parameters['generateParagraphs'] ||
			parameters['customParagraphUpdate']
		) {
			return this.createTopicsPrompts(request, settingsService, true);
		}

		throw new Error('Generation method not yet implemented');
	}

	/**
	 * Process AI response into StudyMaterialSummaryGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			// MULTI-REQUEST AGGREGATION (for multi-topic content generation)
			if (parameters['isMultiRequestAggregation']) {
				console.log(
					'📦 CONTENT-GEN-SERVICE: Starting multi-request aggregation',
				);

				const results = parameters['multiRequestResults'];
				const previousStepResults = parameters['previousStepResults'];
				const topicsStepResult = previousStepResults?.['topics'];

				if (
					!topicsStepResult ||
					!topicsStepResult.gameData ||
					!topicsStepResult.gameData.topics
				) {
					return {
						success: false,
						error: 'Cannot aggregate: no topics from previous step',
						rawResponse: 'Multi-request aggregation failed',
					};
				}

				const originalTopics = topicsStepResult.gameData.topics;

				// Create final game data starting with all topics from step 1
				const finalGameData = new StudyMaterialSummaryGameData();
				finalGameData.title = topicsStepResult.gameData.title || '';
				finalGameData.summary = topicsStepResult.gameData.summary || '';

				// Deep clone topics to preserve original structure
				finalGameData.topics = originalTopics.map(
					(topic: StudyMaterialSummaryEditorTopic) => {
						const newTopic = new StudyMaterialSummaryEditorTopic();
						newTopic.title = topic.title;
						newTopic.summary = topic.summary || '';
						newTopic.paragraphs = topic.paragraphs || [];
						newTopic.tags = topic.tags || [];
						newTopic.intendation = topic.intendation || 0;
						return newTopic;
					},
				);

				// Merge content from each successful result
				let successCount = 0;
				let failCount = 0;

				results.forEach(
					(
						result: GenerationResult & {
							topicIndex?: number;
							topicTitle?: string;
						},
					) => {
						const resultGameData = result.gameData as
							| StudyMaterialSummaryGameData
							| undefined;
						if (
							result.success &&
							resultGameData?.topics?.[0]?.paragraphs &&
							result.topicIndex !== undefined
						) {
							// Add paragraphs to the corresponding topic
							const topicIndex = result.topicIndex;
							if (
								topicIndex >= 0 &&
								topicIndex < finalGameData.topics.length
							) {
								finalGameData.topics[topicIndex].paragraphs =
									resultGameData.topics[0].paragraphs;
								successCount++;
								console.log(
									`   ✓ Merged content for topic [${topicIndex}]: "${result.topicTitle}"`,
								);
							} else {
								console.warn(
									`   ✗ Invalid topic index ${topicIndex} for topic "${result.topicTitle}"`,
								);
								failCount++;
							}
						} else {
							failCount++;
							console.warn(
								`   ✗ Failed result for topic "${result.topicTitle}": ${result.error}`,
							);
						}
					},
				);

				console.log(
					`📦 CONTENT-GEN-SERVICE: Aggregation complete. Success: ${successCount}, Failed: ${failCount}`,
				);

				// Log final gameData structure for debugging
				console.log(
					`📦 CONTENT-GEN-SERVICE: Final gameData structure:`,
					{
						title: finalGameData.title,
						summary: finalGameData.summary,
						topicsCount: finalGameData.topics.length,
						topicsWithParagraphs: finalGameData.topics.filter(
							(t) => t.paragraphs && t.paragraphs.length > 0,
						).length,
						topics: finalGameData.topics.map((t) => ({
							title: t.title,
							paragraphCount: t.paragraphs?.length || 0,
							hasParagraphs:
								!!t.paragraphs && t.paragraphs.length > 0,
							isClassInstance:
								t.constructor?.name ===
								'StudyMaterialSummaryEditorTopic',
						})),
					},
				);

				return {
					success: true,
					gameData: finalGameData,
					rawResponse: `Multi-request aggregation: ${successCount} topics generated, ${failCount} failed`,
				};
			}

			// For title extraction (auto-generation)
			if (parameters['autoGeneration']) {
				const title = response.json?.title;

				if (!title || typeof title !== 'string') {
					return {
						success: false,
						error: 'Invalid response format: no title found',
						rawResponse: response.rawResponse,
					};
				}

				// Create basic game data with extracted title
				const gameData = new StudyMaterialSummaryGameData();
				gameData.title = title;
				gameData.summary = '';
				gameData.topics = [];

				return {
					success: true,
					gameData,
					rawResponse: response.rawResponse,
				};
			}

			// For title suggestions
			if (parameters['generateTitleSuggestions']) {
				const suggestions = response.json?.suggestions;

				if (
					!suggestions ||
					!Array.isArray(suggestions) ||
					suggestions.length === 0
				) {
					return {
						success: false,
						error: 'Invalid response format: no titles found',
						rawResponse: response.rawResponse,
					};
				}

				// Create a game data object with all suggestions
				// Store suggestions in a special format using topics array
				const gameData = new StudyMaterialSummaryGameData();
				gameData.title = ''; // No single title for suggestions
				gameData.summary = ''; // No single summary for suggestions

				// Store each suggestion as a topic with title and summary
				// This allows us to return all suggestions through the existing structure
				gameData.topics = suggestions.map((suggestion: any) => ({
					title: suggestion.title || '',
					summary: suggestion.summary || '',
					paragraphs: [],
					tags: [],
					intendation: 0,
				}));

				return {
					success: true,
					gameData,
					rawResponse: response.rawResponse,
					// Include suggestions in the result for easier access
					suggestions: suggestions,
				} as GenerationResult & { suggestions?: any[] };
			}

			// Check stepType first (modern multi-step approach), then fall back to parameter flags (backward compatibility)
			const stepType = parameters['stepType'];

			// For topics generation, full structure generation, paragraph generation, or comprehensive title and structure generation
			if (
				stepType === 'generateTopics' ||
				stepType === 'generateParagraphs' ||
				stepType === 'titleAndFullStructure' ||
				parameters['generateTopics'] ||
				parameters['fullStructure'] ||
				parameters['titleAndFullStructure'] ||
				parameters['generateParagraphs'] ||
				parameters['customParagraphUpdate']
			) {
				const generatedTopics = response.json?.topics;
				const title = response.json?.title;
				const summary = response.json?.summary;

				if (!generatedTopics || !Array.isArray(generatedTopics)) {
					return {
						success: false,
						error: 'Invalid response format: no topics found',
						rawResponse: response.rawResponse,
					};
				}

				// Create game data with topics
				const gameData = new StudyMaterialSummaryGameData();
				gameData.title = title || '';
				gameData.summary = summary || '';

				// Convert generated topics to StudyMaterialSummaryEditorTopic format
				gameData.topics = generatedTopics.map((topic: any) => ({
					title: topic.title || '',
					summary: '',
					paragraphs: topic.paragraphs || [], // Use generated paragraphs if they exist
					tags: [],
					intendation: topic.intendation || 0,
				}));

				return {
					success: true,
					gameData,
					rawResponse: response.rawResponse,
				};
			}

			// For paragraph length modification
			if (parameters['modifyParagraphLength']) {
				const modifiedText = response.json?.modifiedText;

				if (!modifiedText || typeof modifiedText !== 'string') {
					return {
						success: false,
						error: 'Invalid response format: no modifiedText found',
						rawResponse: response.rawResponse,
					};
				}

				// Return the modified text in a special format
				// The controller will handle updating the specific paragraph
				const gameData = new StudyMaterialSummaryGameData();
				gameData.title = 'Modified Paragraph';
				gameData.summary = modifiedText;
				gameData.topics = [];

				return {
					success: true,
					gameData,
					rawResponse: response.rawResponse,
				};
			}

			// For text content generation (h2/h3 content)
			if (parameters['generateTextContent']) {
				// Enhanced response logging for debugging
				console.log('=== Processing generateTextContent Response ===');
				console.log(
					'Raw response length:',
					response.rawResponse?.length,
				);
				console.log(
					'JSON keys:',
					Object.keys(response.json || {}).join(', '),
				);
				console.log(
					'Has paragraphs key:',
					'paragraphs' in (response.json || {}),
				);
				console.log('Paragraphs value:', response.json?.paragraphs);
				console.log(
					'Paragraphs is array:',
					Array.isArray(response.json?.paragraphs),
				);
				if (response.json?.topics) {
					console.log(
						'Has topics array:',
						response.json.topics.length,
					);
					console.log(
						'First topic has paragraphs:',
						!!response.json.topics[0]?.paragraphs,
					);
				}
				console.log(
					'Full structure preview:',
					JSON.stringify(response.json, null, 2).substring(0, 500),
				);

				const paragraphs = response.json?.paragraphs;

				if (!paragraphs || !Array.isArray(paragraphs)) {
					const errorDetails = `Response had keys: [${Object.keys(response.json || {}).join(', ')}]. Expected 'paragraphs' array at root level. Full response: ${JSON.stringify(response.json).substring(0, 200)}...`;

					console.error(
						'❌ Invalid response format for generateTextContent',
					);
					console.error('Error details:', errorDetails);

					return {
						success: false,
						error: `Invalid response format: no paragraphs found. ${errorDetails}`,
						rawResponse: response.rawResponse,
					};
				}

				// Create game data with paragraph content
				// Convert raw AI paragraphs to StudyMaterialSummaryEditorParagraph format
				const gameData = new StudyMaterialSummaryGameData();
				gameData.title =
					parameters['topicTitle'] || 'Generated Content';
				gameData.summary = '';

				// Transform raw AI paragraphs into StudyMaterialSummaryEditorParagraph objects
				const formattedParagraphs: StudyMaterialSummaryEditorParagraph[] =
					[];

				for (const rawParagraph of paragraphs) {
					const editorParagraph =
						new StudyMaterialSummaryEditorParagraph();
					const textGameData = new TextRendererGameData();

					const imageGameData = new ImageGameData();

					// Check if generating for renderer or editor
					const forRenderer = parameters['forRenderer'] === true;

					// Handle the paragraph content based on AI response format
					if (rawParagraph.type === 'text' && rawParagraph.content) {
						if (forRenderer) {
							// For renderer: use text field (title is in topic, not paragraph)
							textGameData.text = rawParagraph.content.text || '';
						} else {
							// For editor: use pendingText and pendingTitle fields
							textGameData.pendingText =
								rawParagraph.content.text || '';
							textGameData.pendingTitle =
								rawParagraph.content.title || '';
						}

						// Set references if available
						textGameData.references =
							response.json?.references || [];

						editorParagraph.gameData = textGameData;
					}
					if (
						rawParagraph.type === PluginType.Image &&
						rawParagraph.images
					) {
						imageGameData.images = rawParagraph.images.map(
							(image: any) => {
								return {
									caption: image.caption,
									image: this.sourceMaterialService.GetArticleById(
										image.original_image_id,
									),
								};
							},
						);

						editorParagraph.gameData = imageGameData;
					} else if (typeof rawParagraph === 'string') {
						// Handle simple string paragraphs
						if (forRenderer) {
							textGameData.text = rawParagraph;
						} else {
							textGameData.pendingText = rawParagraph;
						}

						// Set references if available
						textGameData.references =
							response.json?.references || [];

						editorParagraph.gameData = textGameData;
					} else if (rawParagraph.text) {
						// Handle paragraphs with direct text property
						if (forRenderer) {
							textGameData.text = rawParagraph.text;
						} else {
							textGameData.pendingText = rawParagraph.text;
						}

						// Set references if available
						textGameData.references =
							response.json?.references || [];

						editorParagraph.gameData = textGameData;
					}

					formattedParagraphs.push(editorParagraph);
				}

				// Store formatted paragraphs in a single topic for processing
				// IMPORTANT: Create proper class instance, not plain object
				const topic = new StudyMaterialSummaryEditorTopic();
				topic.title = parameters['topicTitle'] || '';
				topic.summary = '';
				topic.paragraphs = formattedParagraphs;
				topic.tags = [];
				topic.intendation = 0;
				gameData.topics = [topic];

				return {
					success: true,
					gameData,
					rawResponse: response.rawResponse,
				};
			}

			throw new Error('Response processing method not yet implemented');
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate study material summary specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		const { parameters } = request;

		// Check stepType first (modern multi-step approach), then fall back to parameter flags (backward compatibility)
		const stepType = parameters['stepType'];

		// Check if this is a generation type that can work without source material
		const canWorkWithoutSource =
			stepType === 'generateTopics' ||
			stepType === 'generateContent' ||
			stepType === 'generateParagraphs' ||
			stepType === 'titleAndFullStructure' ||
			parameters['customTopic'] || // Has custom topic specified
			parameters['fullStructure'] || // Full structure generation can work with custom topics
			parameters['generateTopics'] || // Topic generation can work without source
			parameters['titleAndFullStructure'] || // Title and structure can work with custom content
			parameters['generateParagraphs'] || // Paragraph generation can work without source
			parameters['customParagraphUpdate']; // Custom paragraph updates

		// Only require source material if the generation type specifically needs it
		// and no alternative content source is provided
		if (!hasSourceMaterial && !canWorkWithoutSource) {
			return {
				valid: false,
				error: 'Valitse vähintään yksi artikkeli lähteeksi tai anna aihe sisällön luomista varten',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for study material summary
	 */
	getDefaultParameters(): any {
		return {};
	}

	/**
	 * Study material uses two-step generation:
	 * 1. Generate topics structure
	 * 2. Generate content for each topic (parallel requests)
	 * Override the default single-step behavior
	 */
	override getDefaultSteps(): GenerationStep[] {
		return [
			{
				stepId: 'topics',
				stepType: 'generateTopics',
				parameters: {
					generateSubtopics: true,
				},
			},
			{
				stepId: 'content',
				stepType: 'generateContent',
				parameters: {
					allowMultipleParagraphs: true,
				},
				dependsOn: ['topics'],
			},
		];
	}

	override getDefaultBackgroundSteps(): GenerationStep[] {
		return [
			{
				stepId: 'topics',
				stepType: 'generateTopics',
				parameters: {
					generateSubtopics: true,
				},
				modelConfig: AiService.GetModelDefinitionById('gpt-4.1'),
			},
			{
				stepId: 'content',
				stepType: 'generateContent',
				parameters: {
					allowMultipleParagraphs: true,
				},
				dependsOn: ['topics'],
				modelConfig: AiService.GetModelDefinitionById('gpt-4.1'),
			},
		];
	}

	// ============================================
	// TEXT CONTENT GENERATION METHODS
	// ============================================

	/**
	 * Create prompts for text content generation (h2/h3 content)
	 * This is the main router method for text content generation
	 * @param request The generation request with text content parameters
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts for text content generation
	 */
	createTextContentPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		// Determine generation type based on parameters
		const hasSourceMaterial =
			sourceMaterial && sourceMaterial.fileHandles.length > 0;
		const hasExistingParagraphs =
			parameters['existingParagraphs'] &&
			parameters['existingParagraphs'].length > 0;
		const hasCustomRequest =
			parameters['customRequest'] && parameters['customRequest'] !== '';

		let generationType: ContentGenerationType;

		if (hasCustomRequest) {
			generationType = ContentGenerationType.UPDATE_WITH_CUSTOM_REQUEST;
		} else if (hasExistingParagraphs && hasSourceMaterial) {
			generationType = ContentGenerationType.UPDATE_EXISTING_WITH_SOURCE;
		} else if (hasExistingParagraphs && !hasSourceMaterial) {
			generationType =
				ContentGenerationType.UPDATE_EXISTING_WITHOUT_SOURCE;
		} else if (!hasExistingParagraphs && hasSourceMaterial) {
			generationType = ContentGenerationType.NEW_CONTENT_WITH_SOURCE;
		} else {
			generationType = ContentGenerationType.NEW_CONTENT_WITHOUT_SOURCE;
		}

		const params: TextContentGenerationParams = {
			generationType,
			topicTitle: parameters['topicTitle'] || '',
			mainTitle: parameters['mainTitle'] || '',
			topicSummary: parameters['topicSummary'],
			existingParagraphs: parameters['existingParagraphs'],
			selectedConcepts: parameters['selectedConcepts'],
			customRequest: parameters['customRequest'],
			allTopics: parameters['allTopics'],
			allowMultipleParagraphs:
				parameters['allowMultipleParagraphs'] || false,
			imageMetadata: parameters['imageMetadata'],
			context: parameters['context'],
		};

		// Route to appropriate prompt creation method
		switch (generationType) {
			case ContentGenerationType.NEW_CONTENT_WITH_SOURCE:
				return this.createNewContentWithSourcePrompts(
					params,
					settingsService,
				);
			case ContentGenerationType.NEW_CONTENT_WITHOUT_SOURCE:
				return this.createNewContentWithoutSourcePrompts(
					params,
					settingsService,
				);
			case ContentGenerationType.UPDATE_EXISTING_WITH_SOURCE:
				return this.createUpdateContentWithSourcePrompts(
					params,
					settingsService,
				);
			case ContentGenerationType.UPDATE_EXISTING_WITHOUT_SOURCE:
				return this.createUpdateContentWithoutSourcePrompts(
					params,
					settingsService,
				);
			case ContentGenerationType.UPDATE_WITH_CUSTOM_REQUEST:
				return this.createCustomUpdatePrompts(params, settingsService);
			default:
				throw new Error(`Unknown generation type: ${generationType}`);
		}
	}

	/**
	 * Create prompts for new content generation with source material
	 */
	private createNewContentWithSourcePrompts(
		params: TextContentGenerationParams,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja ja alan pedagoginen asiantuntija. Tehtäväsi on kirjoittaa opetustekstiä lähdemateriaalin pohjalta. Lukijan tulee oppia asia kirjoittamasi tekstin pohjalta. Ole siis yksityiskohtainen ja sisällytä mukaan mahdollisimman paljon lähdemateriaalia. Anna vastaus validoidussa JSON-muodossa. Et saa käyttää erikoismerkkejä json-rakenteessa, kuten rivinvaihto tms. JSON-rakenteen pitää olla validoitu ja parsittavissa objektiksi.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		// Add context if provided
		systemPrompt = this.addContextInstructions(
			systemPrompt,
			params.context,
		);

		let userPrompt = `Kirjoita opetustekstiä otsikon "${params.topicTitle}" alle. Tämä otsikko on osa isompaa kokonaisuutta, jonka pääotsikko on "${params.mainTitle}".

Käytä tiedostohakua pääasiallisena lähteenäsi. Sisällytä tekstisisältöön mahdollisimman paljon tietoa ja yksityiskohtia lähdemateriaalista. Käytä hakemiseen file_search_tool-hakutyökalua. Laadi laajat hakusanat materiaalin pääaiheen, mahdollisen tiivistelmän ja tämän osion otsikon pohjalta, jotta löydät hyvää lähdemateriaalia tämän osion otsikkoon liittyen.
`;

		// Add topic summary if provided
		if (params.topicSummary) {
			userPrompt += `\nOsion tiivistelmä, jonka tulee käydä ilmi tekstistä: ${params.topicSummary}\n`;
		}

		// Add concepts if provided
		if (params.selectedConcepts && params.selectedConcepts.length > 0) {
			userPrompt += `\nNämä konseptitoiveet on otettava huomioon. Kirjoita teksti niin, että se kattaa kaikki konseptitoiveet: ${JSON.stringify(params.selectedConcepts)}\n`;
		}

		// Add context about other topics
		userPrompt = this.addTopicsContextInstructions(
			userPrompt,
			params.allTopics,
			params.topicTitle,
		);

		// Add general content instructions
		userPrompt += this.getGeneralContentInstructions();

		// Add output format instructions
		userPrompt += this.getOutputFormatInstructions(
			params.allowMultipleParagraphs,
			params.imageMetadata,
		);

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for new content generation without source material
	 */
	private createNewContentWithoutSourcePrompts(
		params: TextContentGenerationParams,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja ja alan pedagoginen asiantuntija. Tehtäväsi on kirjoittaa opetustekstiä parhaan tietämyksesi mukaan aiheesta. Lukijan tulee oppia asia kirjoittamasi tekstin pohjalta. Ole siis yksityiskohtainen. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		// Add context if provided
		systemPrompt = this.addContextInstructions(
			systemPrompt,
			params.context,
		);

		let userPrompt = `Kirjoita opetustekstiä otsikon "${params.topicTitle}" alle. Tämä otsikko on osa isompaa kokonaisuutta, jonka pääotsikko on "${params.mainTitle}".

Kirjoita parhaan tietämyksesi mukaan tästä aiheesta.
`;

		// Add topic summary if provided
		if (params.topicSummary) {
			userPrompt += `\nOsion tiivistelmä, jonka tulee käydä ilmi tekstistä: ${params.topicSummary}\n`;
		}

		// Add concepts if provided
		if (params.selectedConcepts && params.selectedConcepts.length > 0) {
			userPrompt += `\nNämä konseptitoiveet on otettava huomioon. Kirjoita teksti niin, että se kattaa kaikki konseptitoiveet: ${JSON.stringify(params.selectedConcepts)}\n`;
		}

		// Add context about other topics
		userPrompt = this.addTopicsContextInstructions(
			userPrompt,
			params.allTopics,
			params.topicTitle,
		);

		// Add general content instructions
		userPrompt += this.getGeneralContentInstructions();

		// Add output format instructions
		userPrompt += this.getOutputFormatInstructions(
			params.allowMultipleParagraphs,
			params.imageMetadata,
		);

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for updating existing content with source material
	 */
	private createUpdateContentWithSourcePrompts(
		params: TextContentGenerationParams,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja ja alan pedagoginen asiantuntija. Tehtäväsi on päivittää ja täydentää olemassa olevaa opetustekstiä lähdemateriaalin pohjalta. Lukijan tulee oppia asia kirjoittamasi tekstin pohjalta. Ole siis yksityiskohtainen ja sisällytä mukaan mahdollisimman paljon lähdemateriaalia. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		// Add context if provided
		systemPrompt = this.addContextInstructions(
			systemPrompt,
			params.context,
		);

		let userPrompt = `Annan sinulle taulukon, jossa on tekstikappaleita. Kaikki kappaleet kuuluvat yhteisen otsikon alle, joka on "${params.topicTitle}". Tämä otsikko on osa isompaa kokonaisuutta, jonka pääotsikko on "${params.mainTitle}".

Muotoile kappaleiden sisältöä tai kirjoita ne lähdemateriaalin pohjalta.

<current-paragraphs>
Jos kappaleissa on sisältöä, ota se sisältö lähtökohdaksi uudelle materiaalille ja täydennä sitä. Muutoin voit kirjoittaa sen itse. Palauta sama määrä kappaleita, kuin mitä annettiin.

Tärkeää: jos pendingText-kentässä on materiaalia, käytä VAIN sitä. Jos pendingText-kenttä on tyhjä, käytä tällöin VAIN text-kentän sisältöä.

${JSON.stringify(params.existingParagraphs)}
</current-paragraphs>

Käytä tiedostohakua pääasiallisena lähteenäsi. Sisällytä tekstisisältöön mahdollisimman paljon tietoa ja yksityiskohtia lähdemateriaalista.
`;

		// Add topic summary if provided
		if (params.topicSummary) {
			userPrompt += `\nOsion tiivistelmä, jonka tulee käydä ilmi tekstistä: ${params.topicSummary}\n`;
		}

		// Add concepts if provided
		if (params.selectedConcepts && params.selectedConcepts.length > 0) {
			userPrompt += `\nNämä konseptitoiveet on otettava huomioon: ${JSON.stringify(params.selectedConcepts)}\n`;
		}

		// Add context about other topics
		userPrompt = this.addTopicsContextInstructions(
			userPrompt,
			params.allTopics,
			params.topicTitle,
		);

		// Add general content instructions
		userPrompt += this.getGeneralContentInstructions();

		// Add marking instructions for updates
		userPrompt += `\nOn tärkeää, että merkitset lisätyt asiat <mark> ja </mark>-tageilla. Näin käyttäjä saa tiedon siitä, mitä olet muuttanut tai lisännyt.\n`;

		// Add output format instructions
		userPrompt += this.getOutputFormatInstructions(
			params.allowMultipleParagraphs,
			params.imageMetadata,
		);

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for updating existing content without source material
	 */
	private createUpdateContentWithoutSourcePrompts(
		params: TextContentGenerationParams,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja ja alan pedagoginen asiantuntija. Tehtäväsi on päivittää ja täydentää olemassa olevaa opetustekstiä parhaan tietämyksesi mukaan. Lukijan tulee oppia asia kirjoittamasi tekstin pohjalta. Ole siis yksityiskohtainen. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		// Add context if provided
		systemPrompt = this.addContextInstructions(
			systemPrompt,
			params.context,
		);

		let userPrompt = `Annan sinulle taulukon, jossa on tekstikappaleita. Kaikki kappaleet kuuluvat yhteisen otsikon alle, joka on "${params.topicTitle}". Tämä otsikko on osa isompaa kokonaisuutta, jonka pääotsikko on "${params.mainTitle}".

Muotoile kappaleiden sisältöä tai täydennä niitä parhaan tietämyksesi mukaan.

<current-paragraphs>
Jos kappaleissa on sisältöä, ota se sisältö lähtökohdaksi ja täydennä sitä. Palauta sama määrä kappaleita, kuin mitä annettiin.

Tärkeää: jos pendingText-kentässä on materiaalia, käytä VAIN sitä. Jos pendingText-kenttä on tyhjä, käytä tällöin VAIN text-kentän sisältöä.

${JSON.stringify(params.existingParagraphs)}
</current-paragraphs>
`;

		// Add topic summary if provided
		if (params.topicSummary) {
			userPrompt += `\nOsion tiivistelmä, jonka tulee käydä ilmi tekstistä: ${params.topicSummary}\n`;
		}

		// Add concepts if provided
		if (params.selectedConcepts && params.selectedConcepts.length > 0) {
			userPrompt += `\nNämä konseptitoiveet on otettava huomioon: ${JSON.stringify(params.selectedConcepts)}\n`;
		}

		// Add context about other topics
		userPrompt = this.addTopicsContextInstructions(
			userPrompt,
			params.allTopics,
			params.topicTitle,
		);

		// Add general content instructions
		userPrompt += this.getGeneralContentInstructions();

		// Add marking instructions for updates
		userPrompt += `\nOn tärkeää, että merkitset lisätyt asiat <mark> ja </mark>-tageilla.\n`;

		// Add output format instructions
		userPrompt += this.getOutputFormatInstructions(
			params.allowMultipleParagraphs,
			params.imageMetadata,
		);

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for custom update requests
	 */
	private createCustomUpdatePrompts(
		params: TextContentGenerationParams,
		settingsService: SettingsService,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		let systemPrompt = `Olet opetusmateriaalin kirjoittaja ja alan pedagoginen asiantuntija. Tehtäväsi on muokata opetustekstiä käyttäjän pyynnön mukaisesti. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		// Add context if provided
		systemPrompt = this.addContextInstructions(
			systemPrompt,
			params.context,
		);

		let userPrompt = `Annan sinulle tekstikappaleita, jotka kuuluvat otsikon "${params.topicTitle}" alle. Tämä on osa isompaa kokonaisuutta "${params.mainTitle}".

Käyttäjän pyyntö: ${params.customRequest}

Käyttäjän antama lisäohje on kaikista tärkein ohje ja ylittää muut pyynnöt. Pyri toteuttamaan ohje mahdollisimman tarkasti. Tutki pyyntö ja analysoi, haluaako käyttäjä muuttaa nykyistä tekstisisältöä. Jos pyyntö koskee enemmän tiedon lisäämistä kuin muuttamista, pyri säilyttämään nykyisen tekstin ulkoasu mahdollisimman muuttumattomana.
`;

		// Add existing paragraphs if any
		if (params.existingParagraphs && params.existingParagraphs.length > 0) {
			userPrompt += `\n<current-paragraphs>\n${JSON.stringify(params.existingParagraphs)}\n</current-paragraphs>\n`;
		}

		// Add marking instructions
		userPrompt += `\nMerkitse muutetut tai lisätyt asiat <mark> ja </mark>-tageilla.\n`;

		// Add output format instructions
		userPrompt += this.getOutputFormatInstructions(
			params.allowMultipleParagraphs,
			params.imageMetadata,
		);

		return { system: systemPrompt, user: userPrompt };
	}

	// ============================================
	// UTILITY METHODS FOR PROMPT BUILDING
	// ============================================

	/**
	 * Add context instructions to system prompt
	 */
	private addContextInstructions(
		systemPrompt: string,
		context?: string,
	): string {
		if (context && context !== '') {
			return (
				systemPrompt +
				`\n\nTässä on materiaalin konteksti, joka kuvaa sitä, mistä on kyse. On tärkeää, että kaikki analyysi tehdään suhteessa kontekstiin. <context>${context}</context>`
			);
		}
		return systemPrompt;
	}

	/**
	 * Add instructions about other topics to avoid duplication
	 */
	private addTopicsContextInstructions(
		userPrompt: string,
		allTopics?: any[],
		currentTopicTitle?: string,
	): string {
		if (allTopics && allTopics.length > 0) {
			const otherTopics = allTopics
				.filter((t) => t.title !== currentTopicTitle)
				.map(
					(t) =>
						`Otsake: ${t.title}${t.summary ? ' - tiivistelmä: ' + t.summary : ''}`,
				)
				.join('\n');

			if (otherTopics) {
				userPrompt += `\nTässä tiedoksi koko kokonaisuuden muut otsakkeet. Näiden perusteella voit päätellä, mihin aiheeseen sinun tulee keskittyä tässä otsakkeessa (${currentTopicTitle}) niin, ettet kirjoita samoja asioita uudestaan:\n${otherTopics}\n`;
			}
		}
		return userPrompt;
	}

	/**
	 * Get general content quality instructions
	 */
	private getGeneralContentInstructions(): string {
		return `
On tärkeää, että tekstisisältö vastaa otsikon kuvausta. Tekstissisällöstä täytyy pystyä opetella otsikon kuvaama aihe ammattitasoisesti. Varmista, että kaikki kappaleet ovat johdonmukaisia ja liittyvät toisiinsa. Niiden tulee muodostaa yhtenäinen kokonaisuus, joka kattaa kaikki tärkeät näkökulmat ja yksityiskohdat aiheesta.

Käytä tarvittaessa esimerkkejä tai vertauskuvia havainnollistamaan käsiteltäviä asioita. Vältä liiallista toistoa ja pyri kirjoittamaan selkeästi ja ymmärrettävästi.

Jos otsikko tai lähdemateriaali viittaa jonkinlaisiin ohjeisiin, joissa on eri askelia, on tärkeää, että sisällytät nämä mukaan. Käytä tarvittaessa luetteloita tai numeroituja listoja vaiheiden esittämiseen.

Muista, että tekstisisällön tulee olla yhtenäistä ja johdonmukaista. Älä jätä tekstiä kesken tai aloita uutta aihetta kesken kappaleen.

Jaa kappaleet järkeviin kokonaisuuksiin <p> ja </p>-tageilla.
`;
	}

	/**
	 * Get output format instructions based on parameters
	 */
	private getOutputFormatInstructions(
		allowMultipleParagraphs: boolean,
		imageMetadata?: string,
	): string {
		let instructions = `
JSON-avaimet taulukkona objektin päätasolla:
- paragraphs // taulukko, jossa jokainen objekti on yksi kappale
  * "type": ${PluginType.TextRenderer} // numero, tämä on aina sama arvo tekstisisällölle
  * "text" // string, opetusteksti. Tässä ei ole tarvetta toistaa kappaleen otsikkoa, vaan kirjoita vain itse teksti
  
`;

		// Add image instructions if metadata provided
		if (imageMetadata && imageMetadata !== '') {
			instructions += `
  * "type": ${PluginType.Image} // numero, tämä arvo on aina sama kuvasisällölle
  * "images" // taulukko, jossa jokainen objekti on yksi kuva-objekti
	** "image" // string, kuvan nimi
	** "caption" // string, kuvateksti, joka kertoo miksi kuva on valittu ja mihin haluaan kiinnittää huomiota juuri tässä kohdassa kappaletta
	** "original_image_id" // number, laita tähän kuvan metadatassa ollut "id"-kentän sisältö (numero)
  
`;
		}

		// Add paragraph handling instructions
		if (allowMultipleParagraphs) {
			instructions += `
Älä luo turhaan peräkkäisiä 'text'-tyyppisiä kappaleita. Jos useampi text-tyyppinen kappale on peräkkäin, yhdistä niiden tekstisisältö ja käytä <p> ja </p>-tageja kappalejakojen tekemiseen. 'text'-tyyppiset kappaleet ovat olemassa sen takia, että voit lisätä tekstikappaleiden väliin kuvia.`;

			if (imageMetadata && imageMetadata !== '') {
				instructions += `

Tässä kuvia aiheeseen liittyen:
<images>${imageMetadata}</images>

Jos kuva on jo kerran luotu 'image'-kappaleeksi, viittaa siihen tekstissä käyttämällä esim. ilmauksia "aiemmassa kuvassa", "edellisessä kuvassa" tai muuta vastaavaa. Älä luo toista 'image'-kappaletta, joka viittaa samaan kuva-id:hen.

Voit kytkeä 'image'-objektiin yhden tai useamman kuvan. Laita kuva tai kuvat images-taulukkoon.

On tärkeää, että viittaat annettuihin kuviin objektissa olevalla "id"-nimisen kentän numerolla. Laita tämä id-numero vastauksen content.original_image_id-kenttään.`;
			}
		} else {
			instructions += `
Vastaa taulukolla, jossa on vain yksi kappale tyyppiä 'text' ja käytä <p> ja </p>-tageja kappalejakojen tekemiseen. Et saa palauttaa useita 'text'-tyyppisiä objekteja, vain yksi.`;
		}

		return instructions;
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: StudyMaterialSummaryGameData,
		newGameData: StudyMaterialSummaryGameData,
		clearPrevious: boolean,
	): StudyMaterialSummaryGameData {
		// For title extraction, always update title
		if (newGameData.title) {
			existingGameData.title = newGameData.title;
		}

		if (clearPrevious) {
			existingGameData.summary = newGameData.summary || '';
			existingGameData.topics = newGameData.topics || [];
		} else {
			// Merge logic for other content types will be implemented
			// when extracting the full generation methods
		}

		return existingGameData;
	}

	/**
	 * Helper method that creates a title first and then does full material generation
	 * This leverages existing extractMainTitle and createTopicsPrompts methods for consistency
	 * @param request The generation request with source material
	 * @param settingsService Settings service for language preferences
	 * @returns Generation result with title and full structure
	 */
	createTitleAndFullStructure(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { sourceMaterial } = request;
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		// Get individual prompts from existing methods
		const titlePrompts = this.extractMainTitle(
			sourceMaterial,
			settingsService,
		);
		const topicsPrompts = this.createTopicsPrompts(
			request,
			settingsService,
			true,
		);

		// Combine prompts with clear instructions
		const systemPrompt = `Olet opetusmateriaalin kirjoittaja. Tehtäväsi on luoda kokonaisvaltainen opetusmateriaalin rakenne lähdemateriaalin pohjalta käyttäen annettuja ohjeita. Luo ensin sopiva otsikko, sitten tiivistelmä, ja lopuksi johdonmukainen sisällysluettelo alaotsikoilla sisältöjen kanssa. Lukijan tulee sisällysluettelon avulla pystyä hahmottamaan koko lähdemateriaalin sisältö ja edetä sen läpi johdonmukaisesti. Anna vastaus validoidussa JSON-muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

		const userPrompt = topicsPrompts.user;

		// 		const userPrompt = `Sinun tulee suorittaa kaksi tehtävää yhdessä. Noudata annettuja ohjeita tarkkaan.

		// TEHTÄVÄ 1: Otsikon luominen
		// Tässä ohjeet otsikon luomiseen:
		// ${titlePrompts.user}

		// TEHTÄVÄ 2: Sisällön luominen
		// Tässä ohjeet sisällön luomiseen:
		// ${topicsPrompts.user}

		// LOPULLINEN TEHTÄVÄ:
		// Yhdistä molemmat tehtävät ja luo kokonaisvaltainen rakenne, joka sisältää:
		// 1. Pääotsikon (TEHTÄVÄ 1:n mukaisesti)
		// 2. Tiivistelmän (TEHTÄVÄ 2:n mukaisesti)
		// 3. Sisällysluettelon otsikoilla ja alaotsikoilla (TEHTÄVÄ 2:n mukaisesti)
		// 4. Jokaiselle otsikolle/aliotsikolle tekstikappale sisältöineen (TEHTÄVÄ 2:n mukaisesti)

		// Anna vastaus samassa JSON-muodossa kuin TEHTÄVÄ 2 vaatii, mutta sisällytä myös TEHTÄVÄ 1:n otsikko "title"-kenttään.`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for generating content for existing topics (multi-step generation - step 2)
	 * Receives topics from previous step and generates paragraphs for each topic
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns Generation prompts
	 */
	createContentGenerationPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		// Get topics from previous step results
		const previousStepResults = parameters?.['previousStepResults'];
		const topicsStepResult = previousStepResults?.['topics'];

		if (
			!topicsStepResult ||
			!topicsStepResult.gameData ||
			!topicsStepResult.gameData.topics
		) {
			throw new Error(
				'Previous step (topics) did not provide topics data',
			);
		}

		const gameData =
			topicsStepResult.gameData as StudyMaterialSummaryGameData;
		const topics = gameData.topics;
		const mainTitle = gameData.title || '';

		// Filter empty topics (like component does in GenerateContentForEmptyTopics line 3470-3474)
		const emptyTopics = topics.filter(
			(topic) =>
				topic.title !== '' &&
				(!topic.paragraphs || topic.paragraphs.length === 0),
		);

		console.log(
			`📝 CONTENT-GEN-SERVICE: Creating prompts for ${emptyTopics.length} empty topics`,
		);

		const hasSourceMaterial =
			sourceMaterial && sourceMaterial.fileHandles.length > 0;

		// Create ONE prompt PER empty topic (like component does in line 3538)
		const multipleRequests = emptyTopics.map((topic, emptyTopicIndex) => {
			// Find the actual index of this topic in the full topics array
			const topicIndex = topics.findIndex((t) => t.title === topic.title);

			console.log(
				`   [${emptyTopicIndex}] Creating prompt for topic "${topic.title}" (index: ${topicIndex})`,
			);

			// Create text content generation parameters for THIS specific topic
			// Using the EXACT same approach as the component (lines 3015-3031)
			const params: TextContentGenerationParams = {
				generationType: hasSourceMaterial
					? ContentGenerationType.NEW_CONTENT_WITH_SOURCE
					: ContentGenerationType.NEW_CONTENT_WITHOUT_SOURCE,
				topicTitle: topic.title,
				mainTitle: mainTitle,
				topicSummary: topic.summary,
				allTopics: topics, // Full context
				allowMultipleParagraphs: true,
				imageMetadata: sourceMaterial?.imagesAsString || '',
				context: parameters['context'],
			};

			// Get prompts for this specific topic using existing method
			const topicPrompts = hasSourceMaterial
				? this.createNewContentWithSourcePrompts(
						params,
						settingsService,
					)
				: this.createNewContentWithoutSourcePrompts(
						params,
						settingsService,
					);

			return {
				system: topicPrompts.system,
				user: topicPrompts.user,
				topicIndex: topicIndex, // Use actual topic index from full array
				topicTitle: topic.title,
			};
		});

		console.log(
			`✅ CONTENT-GEN-SERVICE: Created ${multipleRequests.length} individual prompts`,
		);

		// Return with multipleRequests field (batch-generation.service will detect this)
		return {
			system: '', // Not used when multipleRequests exists
			user: '', // Not used when multipleRequests exists
			multipleRequests: multipleRequests,
		};
	}
}
